<template>
  <sticky-header-layout>
    <template #header>
      <list-page-header>
        <div slot="right">
          <div id="edm-status">
            {{ form.status }}
          </div>
          <el-button
            :disabled="isSaving"
            size="mini"
            type="success"
            icon="el-icon-s-promotion"
            @click="handleTest"
          >
            Test
          </el-button>
          <el-button
            :disabled="isLoading"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >
            {{ $t('button.cancel') }}
          </el-button>
          <el-button
            :disabled="!canSave"
            size="mini"
            type="primary"
            icon="el-icon-upload2"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </div>
      </list-page-header>
    </template>

    <div v-loading="isLoading">
      <el-form
        ref="form"
        :disabled="isLoading"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="top"
        label-width="200"
      >        
        <el-form-item
          label="Subject"
          prop="subject"
        >
          <el-input
            v-model="form.subject"
            clearable
          />
        </el-form-item>

        <el-form-item
          label="Publish At"
          prop="publish_at"
        >
          <el-date-picker
            v-model="form.publish_at"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="Publish At"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item
          v-if="canUpdateStatus"
          label="Status"
          prop="status"
        >
          <el-radio 
            v-model="form.status" 
            label="draft"
          >
            Draft
          </el-radio>
          
          <el-radio 
            v-model="form.status"
            label="scheduled"
          >
            Scheduled
          </el-radio>
        </el-form-item>

        <el-form-item
          v-else
          label="Status"
          prop="status"
        >
          <el-input
            v-model="form.status"
            :disabled="true"
          />
        </el-form-item>

        <div v-for="section in form.sections">
          <h4 class="title">Section {{ section.sort }}</h4>

          <el-form-item
            label="Image URL"
            prop="image_url"
          >
            <el-input
              v-model="section.image_url"
              clearable
            />
          </el-form-item>

          <el-form-item
            label="Main URL"
            prop="url"
          >
            <el-input
              v-model="section.url"
              clearable
            />
          </el-form-item>

          <div class="edm-clearfix"/>
        </div>

        <div v-if="canSave">
          <el-button
            :disabled="isSaving"
            size="mini"
            type="info"
            icon="el-icon-plus"
            style="margin-top: 10px;"
            @click="handleAddSection"
          >
            Add Section
          </el-button>
        </div>
      </el-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import { reactive } from '@vue/composition-api'
import EdmService from '../../api/edm'
import ListPageHeader from '@/views/layout/components/ListPageHeader.vue'
import { i18n } from '@/lang'
import { formatDatetime } from '../../utils/datetime'

export default {
  name: 'TBREdmDetail',
  components: {
    ListPageHeader,
  },
  props: {
    id: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    const fromRule = reactive({
      subject: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
    })

    return {
      isLoading: true,
      form: {
        subject: '',
        confirmed_by: 0,
        publish_at: '',
        status: '',
        sections: [],
      },
      formRules: fromRule,
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
    canUpdateStatus() {
      return this.form.confirmed_by > 0 && (this.form.status === 'draft' || this.form.status === 'scheduled')
    },
    canSave() {
      return !this.loading && !(this.form.status === 'sending' || this.form.status === 'completed')
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail(this.id) 
    } else {
      this.addSection('', '')
      this.isLoading = false
    }
  },
  methods: {
    formatDatetimeToUTC(date) {
      return formatDatetime({ date: date, timezoneOffset: -8})
    },
    async getDetail() {
      this.isLoading = true

      try {
        const response = await EdmService.getTBREdmDetail(this.id)
        const { data } = response

        this.form.subject = data.subject
        this.form.confirmed_by = data.confirmed_by
        this.form.publish_at = data.publish_at ? formatDatetime({date: data.publish_at}) : ''
        this.form.status = data.status

        for (let i = 0; i < data.sections.length; i++) {
          this.addSection(data.sections[i].url, data.sections[i].image_url)
        }
        
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
    async createTBREdm(form) {
      this.isLoading = true

      try {
        const result = await EdmService.createTBREdm(form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async updateTBREdm(id, form) {
      this.isLoading = true

      try {
        const result = await EdmService.updateTBREdm(id, form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async testTBREdm(id) {
      this.isLoading = true

      try {
        const result = await EdmService.testTBREdm(id)
        this.handleTestSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    addSection(url, imageUrl) {
      let section = {
        url: url,
        image_url: imageUrl,
        sort: this.form.sections.length + 1,
      }

      this.form.sections.push(section)
    },
    handleAddSection() {
      this.addSection('', '')
    },
    async handleSave() {
      try {
        if (this.form.publish_at) {
          this.form.publish_at = this.formatDatetimeToUTC(this.form.publish_at)
        }

        await this.$refs.form.validate()

        if (this.isEditMode) {
          this.updateTBREdm(this.id, this.form)
        } else {
          this.createTBREdm(this.form)
        }
      } catch (error) {
        this.$alert('Please fill in all the required information.', 'Oops..', {
          confirmButtonText: 'OK',
        })
      }
    },
    async handleTest() {
      try {
        await this.$refs.form.validate()

        if (this.isEditMode) {
          this.testTBREdm(this.id)
        }
      } catch (error) {
        this.$alert('Please fill in all the required information.', 'Oops..', {
          confirmButtonText: 'OK',
        })
      }
    },
    handleCancel() {
      this.$router.push({ path: `/edm/list-tbr-edm` })
    },
    handleSaveSuccess() {
      this.isLoading = false
      this.$router.push({ path: `/edm/list-tbr-edm` })
    },
    handleSaveFail(error) {
      this.isLoading = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: 'OK',
      })
    },
    handleTestSuccess(error) {
      this.isLoading = false
      this.$alert('The Edm has been sent to your mailbox.', 'Success', {
        confirmButtonText: 'OK',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";
@import "~@/assets/styles/table.scss";

#edm-status {
  float: left;
  margin-right: 16px;
  line-height: 30px;
  font-weight: bold;
  text-transform: uppercase;
}

.edm-clearfix {
  clear: both;
}
</style>